import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetDoctorAccountQuery, useUpdateDoctorAccountMutation } from '../../store/api/doctorApi';
import { useDoctorLogoutMutation } from '../../store/api/doctorApi';
import { logout } from '../../store/slices/authSlice';
import { useAppDispatch } from '../../hooks/redux';
import { showSuccess, showError } from '../../utils/toast';
import PageLoader from '../../components/PageLoader';

const DoctorAccount: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data, isLoading } = useGetDoctorAccountQuery(undefined);
  const [updateAccount, { isLoading: isUpdating }] = useUpdateDoctorAccountMutation();
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [logoutMutation] = useDoctorLogoutMutation();
  
  const [emailFormData, setEmailFormData] = useState({
    email: '',
  });
  
  const [passwordFormData, setPasswordFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    if (data?.data) {
      setEmailFormData({ email: data.data.email || '' });
    }
  }, [data]);

  const handleLogout = async () => {
    try {
      await logoutMutation(undefined).unwrap();
      dispatch(logout());
      showSuccess('Logged out successfully!');
      navigate('/doctor/login');
    } catch (err: any) {
      showError(err?.data?.message || 'Logout failed. Please try again.');
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingEmail(true);
    
    try {
      await updateAccount({ email: emailFormData.email }).unwrap();
      showSuccess('Email updated successfully! Please login again.');
      setTimeout(() => {
        handleLogout();
      }, 2000);
    } catch (err: any) {
      showError(err?.data?.message || 'Email update failed');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingPassword(true);
    
    try {
      if (passwordFormData.new_password !== passwordFormData.confirm_password) {
        showError('New Password and Confirm Password do not match!');
        return;
      }
      if (passwordFormData.new_password.length < 6) {
        showError('New Password must be at least 6 characters long!');
        return;
      }
      await updateAccount({
        old_password: passwordFormData.old_password,
        new_password: passwordFormData.new_password,
        confirm_password: passwordFormData.confirm_password,
      }).unwrap();
      showSuccess('Password updated successfully! Please login again.');
      setTimeout(() => {
        handleLogout();
      }, 2000);
    } catch (err: any) {
      showError(err?.data?.message || 'Password update failed');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Change Email or Password
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/doctor/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Account</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <div className="mb-8 text-center">
                  
                </div>
                <div className="row g-9 mb-8">
                  <div className="col-md-12">
                    <form 
                      id="kt_modal_new_target_form_email" 
                      className="form fv-plugins-bootstrap5 fv-plugins-framework" 
                      onSubmit={handleEmailSubmit}
                    >
                      <div className="d-flex flex-column fv-row fv-plugins-icon-container">
                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                          <span className="required">Email</span>
                        </label>
                        <input 
                          type="email" 
                          required 
                          className="form-control form-control-solid" 
                          placeholder="Enter Email" 
                          name="email" 
                          value={emailFormData.email}
                          onChange={(e) => setEmailFormData({ email: e.target.value })}
                        />
                      </div>
                      <button 
                        type="submit" 
                        className="btn btn-base my-3" 
                        name="change_email"
                        disabled={isUpdatingEmail}
                      >
                        {isUpdatingEmail ? (
                          <>
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </>
                        ) : (
                          'Update Email'
                        )}
                      </button>
                    </form>
                  </div>
                  
                  <form 
                    id="kt_modal_new_target_form_password" 
                    className="form fv-plugins-bootstrap5 fv-plugins-framework" 
                    onSubmit={handlePasswordSubmit}
                  >
                    <div className="col-md-12">
                      <div className="row">
                        <div className="col-md-12 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Old(Current) Password</label>
                          <input 
                            type="password" 
                            required 
                            className="form-control form-control-solid" 
                            placeholder="Enter Your Old Password" 
                            name="old_password"
                            value={passwordFormData.old_password}
                            onChange={(e) => setPasswordFormData({ ...passwordFormData, old_password: e.target.value })}
                          />
                          <br />
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">New Password</label>
                          <input 
                            type="password" 
                            required 
                            className="form-control form-control-solid" 
                            minLength={6}
                            placeholder="Enter New Password" 
                            name="new_password"
                            value={passwordFormData.new_password}
                            onChange={(e) => setPasswordFormData({ ...passwordFormData, new_password: e.target.value })}
                          />
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Confirm Password</label>
                          <input 
                            type="password" 
                            required 
                            className="form-control form-control-solid" 
                            minLength={6}
                            placeholder="Enter Confirm Password" 
                            name="confirm_password"
                            value={passwordFormData.confirm_password}
                            onChange={(e) => setPasswordFormData({ ...passwordFormData, confirm_password: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-left">
                      <button 
                        type="submit" 
                        id="kt_modal_new_target_submit" 
                        name="change_password" 
                        className="btn btn-base"
                        disabled={isUpdatingPassword}
                      >
                        {isUpdatingPassword ? (
                          <span className="indicator-progress">Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>
                        ) : (
                          <span className="indicator-label">Change Password</span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorAccount;
